/**
 * Permission Context Provider
 *
 * Provides global permission context and utilities throughout the application.
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { setCurrentCompanyUser, updateCompanyUserPermissions } from '../store/slices/authSlice';
import { FeaturePermission } from '../constants/featurePermissions';
import { PermissionContext as IPermissionContext, PermissionChecker, PermissionValidator } from '../utils/permissionChecker';
import { getPermissionContext, getCurrentCompanyUser } from '../services/permissionService';
import {companyService} from '../services/companyService';
import { setCurrentCompany } from "../store/slices/companiesSlice";
import cookieService from '../services/cookieService';

// Extended permission context interface
interface ExtendedPermissionContext extends IPermissionContext {
  checker: PermissionChecker;
  validator: PermissionValidator;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  migratePermissions: () => Promise<void>;
}

// Create the context
const PermissionContext = createContext<ExtendedPermissionContext | null>(null);

// Permission provider props
interface PermissionProviderProps {
  children: ReactNode;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

/**
 * Permission Provider Component
 */
export const PermissionProvider: React.FC<PermissionProviderProps> = ({
  children,
  autoRefresh = false,
  refreshInterval = 5 * 60 * 1000 // 5 minutes
}) => {
  const dispatch = useDispatch();
  const { user, currentCompanyUser } = useSelector((state: RootState) => state.auth);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create permission context
  const permissionContext: IPermissionContext = {
    userPermissions: (currentCompanyUser?.featurePermissions as FeaturePermission[]) || [],
    userRole: user?.role || '',
    companyUser: currentCompanyUser,
    isAdmin: user?.role === 'admin',
    isSuperAdmin: user?.role === 'super_admin',
    isAuthenticated: !!user
  };

  // Create checker and validator
  const checker = new PermissionChecker(permissionContext);
  const validator = new PermissionValidator(permissionContext);

  // Refresh permissions from server
  const refresh = async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      let companyUsers = await companyService.getCompanies();
      if(companyUsers.data.length > 0){
        // Check if there's a saved company ID in cookie
        const savedCompanyId = cookieService.get('currentCompanyId');
        let selectedCompany = companyUsers.data[0];

        if (savedCompanyId) {
          // Find the saved company in the list
          const savedCompany = companyUsers.data.find((c: any) => c._id === savedCompanyId);
          if (savedCompany) {
            selectedCompany = savedCompany;
          }
        }

        dispatch(setCurrentCompany(selectedCompany));
        // Cookie will be saved by the setCurrentCompany action in Redux

        const context = await getPermissionContext(selectedCompany);
        dispatch(setCurrentCompanyUser(context.companyUser));
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh permissions';
      setError(errorMessage);
      console.error('Permission refresh failed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Migrate user permissions
  const migratePermissions = async () => {
    if (!user || !currentCompanyUser) return;

    setIsLoading(true);
    setError(null);

    try {
      const { migrateUserPermissions } = await import('../services/permissionService');
      const migratedUser = await migrateUserPermissions();
      dispatch(updateCompanyUserPermissions({
        featurePermissions: migratedUser.featurePermissions as FeaturePermission[]
      }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to migrate permissions';
      setError(errorMessage);
      console.error('Permission migration failed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-refresh permissions
  useEffect(() => {
    if (!autoRefresh || !user) return;

    const interval = setInterval(refresh, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, user]);

  const contextValue: ExtendedPermissionContext = {
    ...permissionContext,
    checker,
    validator,
    isLoading,
    error,
    refresh,
    migratePermissions
  };

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
};

/**
 * Hook to use permission context
 */
export const usePermissionContext = (): ExtendedPermissionContext => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissionContext must be used within a PermissionProvider');
  }
  return context;
};

/**
 * Permission Status Component
 */
export const PermissionStatus: React.FC = () => {
  const { isLoading, error, userPermissions, userRole, refresh } = usePermissionContext();

  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 bg-white shadow-lg border border-gray-200 rounded-lg p-4 max-w-sm">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-gray-900">Permission Status</h4>
        <button
          onClick={refresh}
          disabled={isLoading}
          className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
        >
          {isLoading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      <div className="space-y-1 text-xs">
        <div>Role: <span className="font-mono">{userRole}</span></div>
        <div>Permissions: <span className="font-mono">{userPermissions.length}</span></div>
        {error && (
          <div className="text-red-600 bg-red-50 p-2 rounded">
            Error: {error}
          </div>
        )}
      </div>

      {userPermissions.length > 0 && (
        <details className="mt-2">
          <summary className="text-xs cursor-pointer text-gray-600">View Permissions</summary>
          <div className="mt-1 text-xs font-mono text-gray-500 max-h-32 overflow-y-auto">
            {userPermissions.map((permission, index) => (
              <div key={index}>{permission}</div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

/**
 * Permission Error Boundary
 */
interface PermissionErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class PermissionErrorBoundary extends React.Component<
  { children: ReactNode; fallback?: ReactNode },
  PermissionErrorBoundaryState
> {
  constructor(props: { children: ReactNode; fallback?: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): PermissionErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Permission system error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-900">Permission System Error</h3>
              <p className="text-sm text-red-700">
                There was an error with the permission system. Please refresh the page.
              </p>
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <pre className="text-xs text-red-600 mt-2 overflow-auto">
                  {this.state.error.message}
                </pre>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Permission Loading Component
 */
export const PermissionLoader: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isLoading, error } = usePermissionContext();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading permissions...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-900">Permission Warning</h3>
            <p className="text-sm text-yellow-700">
              Unable to load permissions. Some features may not be available.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

/**
 * Permission Migration Prompt
 */
export const PermissionMigrationPrompt: React.FC = () => {
  const { userPermissions, userRole, migratePermissions, isLoading } = usePermissionContext();
  const [dismissed, setDismissed] = useState(false);

  // Only show if user has no feature permissions but has a role (needs migration)
  const needsMigration = userPermissions.length === 0 && userRole && userRole !== 'tenant';

  if (!needsMigration || dismissed) {
    return null;
  }

  const handleMigrate = async () => {
    await migratePermissions();
    setDismissed(true);
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-blue-900">Permission Update Available</h3>
          <p className="text-sm text-blue-700 mt-1">
            Your account can be upgraded to use the new permission system for better security and features.
          </p>
          <div className="mt-3 flex space-x-3">
            <button
              onClick={handleMigrate}
              disabled={isLoading}
              className="bg-blue-600 text-white px-3 py-1 text-sm rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Updating...' : 'Update Now'}
            </button>
            <button
              onClick={() => setDismissed(true)}
              className="text-blue-600 px-3 py-1 text-sm rounded hover:bg-blue-100"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};